import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  AutoComplete,
  type AutoCompleteChangeEvent,
  type AutoCompleteCompleteEvent,
  type AutoCompleteSelectEvent,
} from 'primereact/autocomplete'
import { searchProxyUsers } from '../api/userProxyApi'
import type { ProxyUserOption } from '../types'
import { proxyUserLabel } from './proxyUserLabel'

/** En az bu kadar karakterde arama tetiklenir (paylaşılan `MIN_SEARCH_LEN` ile aynı eşik). */
const MIN_CHARS = 2

interface Props {
  /** Seçili kullanıcı; `null` = seçim yok (filtrede "tümü", formda boş). */
  value: ProxyUserOption | null
  onChange: (user: ProxyUserOption | null) => void
  placeholder: string
  ariaLabel: string
  /** Kök öğeye eklenecek ek sınıf (filtre çubuğunda daha dar durur). */
  className?: string
  disabled?: boolean
  /** Odağı bu alana ver (form açılışı). */
  autoFocus?: boolean
}

/**
 * **Kullanıcı seçicisi** (090) — ad, soyad **veya sicil** ile arayıp tek kullanıcı seçer.
 *
 * Ekranın **üç** kullanım yerinde de aynı bileşen kullanılır (Vekalet Veren filtresi · Vekalet Verilen
 * filtresi · yeni kayıt formunun iki alanı), böylece arama davranışı ve panel görünümü yerlere göre
 * ayrışmaz (Reuse Mandate).
 *
 * **Kaynak `USERS`, HR (SAP) değil:** `USERS_PROXY` kolonları `USERS`'a FK'lidir. Bu yüzden 039'un
 * `useEmployeeSearch`/`SinglePersonEditor` çifti **yeniden kullanılMADI** — onlar HR sicilini arar ve
 * sicilden kullanıcı kimliğine güvenilir bir dönüş garantisi yoktur (uygulamada kaydı olmayan bir
 * sicil seçilebilir ve kayıt FK'de patlardı).
 *
 * Sonuçlar **yerel state**'te tutulur ve her aramada **yeni referanslı** dizi yazılır: PrimeReact
 * AutoComplete panelini yalnız `suggestions` referansı değiştiğinde açar, React Query verisi doğrudan
 * bağlanırsa aynı terim (cache) tekrar arandığında referans değişmez ve panel açılmaz (039'da
 * belgelenen tuzak). Önbellek yine korunur (`fetchQuery` + `staleTime`); yarış koruması seq guard ile.
 */
export default function ProxyUserPicker({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
  disabled,
  autoFocus,
}: Readonly<Props>) {
  const qc = useQueryClient()
  const [suggestions, setSuggestions] = useState<ProxyUserOption[]>([])
  const [query, setQuery] = useState(() => proxyUserLabel(value?.name, value?.surname))
  const seqRef = useRef(0)

  /**
   * Seçimi bu bileşenin KENDİSİ düşürdüğünde (kullanıcı seçili metni düzenlemeye başladı) aşağıdaki
   * efektin kutuyu boşaltmasını engelleyen tek-seferlik bayrak.
   *
   * Neden gerekli: `onQueryChange` seçim varken metin değişince `onChange(null)` çağırır. Bayrak
   * olmasa `value` `null`'a düşer → efekt koşar → `setQuery('')` → PrimeReact DOM input'unu
   * `props.value` ile zorla eşitler ve **tek bir Backspace kullanıcının yazdığı metnin tamamını
   * siler** (arama paneli de boş kutunun üstünde açık kalır). Bağımsız code review'da yakalandı.
   */
  const selfCleared = useRef(false)

  /**
   * Seçim DIŞARIDAN değiştiğinde (ör. "Temizle" tüm filtreleri sıfırlar, ya da form yeniden açılır)
   * kutunun metni de izler. Kendi düşürdüğümüz seçimde metne dokunulmaz — kullanıcı yazmaya devam eder
   * ve alan yeni bir seçim bekler.
   */
  useEffect(() => {
    if (selfCleared.current) {
      selfCleared.current = false
      return
    }
    setQuery(proxyUserLabel(value?.name, value?.surname))
  }, [value])

  const complete = async (e: AutoCompleteCompleteEvent) => {
    const q = (e.query ?? '').trim()
    const seq = ++seqRef.current
    if (q.length < MIN_CHARS) {
      setSuggestions([]) // taze boş → panel kapanır
      return
    }
    try {
      const data = await qc.fetchQuery({
        // Kök anahtar liste sorgusundan (`admin-user-proxies`) AYRI: React Query prefix ile eşleşir,
        // aynı kökü paylaşsalardı ekle/kaldır mutation'larının `invalidateQueries({queryKey:[KEY]})`
        // çağrısı bu arama girdilerini de geçersiz kılar ve aşağıdaki `staleTime` boşa çıkardı.
        queryKey: ['admin-proxy-user-search', q],
        queryFn: () => searchProxyUsers(q),
        staleTime: 60_000,
      })
      if (seq === seqRef.current) setSuggestions([...data]) // HER seferinde taze referans
    } catch {
      if (seq === seqRef.current) setSuggestions([])
    }
  }

  /**
   * Kutu metni değişti. Kullanıcı metni **boşaltırsa** seçim de kalkar (`onChange(null)`) — filtrede
   * bu "tümü"ye dönmek, formda alanı boşaltmaktır. Metni bozup seçimi bırakmak istemsiz bir eşleşme
   * yaratacağından, seçili bir kullanıcı varken metin değiştiğinde seçim **düşürülür**: alan yeniden
   * seçim bekler.
   */
  const onQueryChange = (e: AutoCompleteChangeEvent) => {
    const v = e.value
    const text =
      typeof v === 'string'
        ? v
        : proxyUserLabel((v as ProxyUserOption)?.name, (v as ProxyUserOption)?.surname)
    setQuery(text)
    if (value != null && text !== proxyUserLabel(value.name, value.surname)) {
      // Seçimi düşür ama YAZILAN METNE DOKUNMA (bkz. `selfCleared`).
      selfCleared.current = true
      onChange(null)
    }
  }

  const onSelect = (e: AutoCompleteSelectEvent) => {
    const picked = e.value as ProxyUserOption
    setQuery(proxyUserLabel(picked?.name, picked?.surname))
    onChange(picked ?? null)
  }

  return (
    <AutoComplete
      value={query}
      suggestions={suggestions}
      completeMethod={complete}
      delay={400}
      minLength={MIN_CHARS}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      autoFocus={autoFocus}
      onChange={onQueryChange}
      onSelect={onSelect}
      panelClassName="admin-proxy-user-panel"
      className={`admin-proxy-user-input${className ? ` ${className}` : ''}`}
      itemTemplate={(item: ProxyUserOption) => (
        <div className="admin-proxy-user-item">
          <span className="admin-proxy-user-name">{proxyUserLabel(item.name, item.surname)}</span>
          {item.employeeId && <span className="admin-proxy-user-sicil">{item.employeeId}</span>}
        </div>
      )}
    />
  )
}
